import { CategorySettingsDialog } from "@/components/CategorySettingsDialog";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { AuthImg } from "@/components/studio/AuthImg";
import { api } from "@/lib/api";

const num = (x) => (Number.isFinite(parseFloat(x)) ? parseFloat(x) : 1e9);

const Mark = ({ ok, label, testId }) => (
  <span className={`inline-flex items-center gap-1 ${ok ? "text-[#2D5A3F]" : "text-[#9B2C2C]/80"}`} data-testid={testId}>
    {ok ? <Check size={11} /> : <Minus size={11} />}{label}
  </span>
);

const styleOf = (p, cat) => (p.art_style ? (cat.art_style && p.art_style.trim() === cat.art_style.trim() ? "series" : "own") : cat.art_style ? "series" : "none");

const Book = ({ p, cat }) => {
  const st = styleOf(p, cat);
  const full = p.illustration_path && p.back_art_path && p.chapter_drawn >= p.chapter_total && p.chapter_total > 0;
  return (
    <Link to={`/products/${p.id}`} className="group block w-44 shrink-0" data-testid={`series-book-${p.id}`}>
      <div className="relative transition-transform duration-300 group-hover:-translate-y-1 shadow-sm group-hover:shadow-md">
        <AuthImg path={`/products/${p.id}/cover-thumb.jpg`} version={`${p.updated_at}-${p.illustration_path}-${cat.theme?.dark}`} alt={p.title} className="w-44 aspect-[4/5] object-cover" testId={`series-cover-${p.id}`} />
        {full && <span className="absolute top-2 right-2 text-[9px] tracking-[0.2em] px-1.5 py-0.5" style={{ background: cat.theme?.accent, color: cat.theme?.dark }}>FULL SET</span>}
      </div>
      <div className="mt-3 text-sm leading-snug line-clamp-2 font-body-serif">{p.issue_no ? <span className="italic text-[#5C5255]">No. {p.issue_no} · </span> : null}{p.title}</div>
      <div className="mt-2 flex flex-wrap gap-x-2.5 gap-y-1 text-[11px]">
        <Mark ok={!!p.illustration_path} label="Cover" testId={`series-mark-cover-${p.id}`} />
        <Mark ok={!!p.back_art_path} label="Back" testId={`series-mark-back-${p.id}`} />
        <Mark ok={p.chapter_total > 0 && p.chapter_drawn >= p.chapter_total} label={`Ch ${p.chapter_drawn}/${p.chapter_total}`} testId={`series-mark-chapters-${p.id}`} />
      </div>
      <div className={`mt-1 text-[11px] ${st === "series" ? "text-[#5C5255]" : "text-[#B7791F]"}`} data-testid={`series-style-${p.id}`}>
        {st === "series" ? "Series style" : st === "own" ? (cat.art_style ? "Own style — differs from series" : "Own style") : "No style yet"}
      </div>
    </Link>
  );
};

const Shelf = ({ cat, books, onEdit }) => {
  const t = cat.theme || {};
  const full = books.filter((p) => p.illustration_path && p.back_art_path && p.chapter_total > 0 && p.chapter_drawn >= p.chapter_total).length;
  const own = cat.art_style ? books.filter((p) => styleOf(p, cat) !== "series").length : 0;
  return (
    <section className="mb-14" data-testid={`series-shelf-${cat.id}`}>
      <div className="px-6 py-5 flex flex-wrap items-end justify-between gap-4" style={{ background: t.dark }}>
        <div className="max-w-3xl">
          <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: t.accent }}>{t.name || "Series"} · {books.length} {books.length === 1 ? "title" : "titles"}</div>
          <h2 className="font-playfair text-3xl text-[#F3EBD6] mt-1">{cat.name}</h2>
          <p className="text-xs text-[#EDE3C8]/75 mt-2 italic line-clamp-2" data-testid={`series-style-text-${cat.id}`}>
            {cat.art_style || "No series style yet. Set one from any title's Illustrated set card and tick “Use as the series style”."}
          </p>
        </div>
        <div className="text-xs text-[#EDE3C8]/85 text-right" data-testid={`series-summary-${cat.id}`}>
          <button onClick={() => onEdit(cat)} className="mb-2 px-3 py-1.5 border text-[11px] tracking-[0.15em] uppercase hover:bg-white/10 transition-colors" style={{ borderColor: t.accent, color: t.accent }} data-testid={`series-edit-category-${cat.id}`}>Colours & settings</button>
          <div className="mb-1 text-[#EDE3C8]/70" data-testid={`series-model-${cat.id}`}>Writes with {cat.writing_model || "the default model"}</div>
          <div><span style={{ color: t.accent }}>{full}</span> of {books.length} fully illustrated</div>
          <div>{!cat.art_style ? "No series style set" : own ? <><span style={{ color: t.accent }}>{own}</span> not on the series style</> : "All on the series style"}</div>
        </div>
      </div>
      <div className="flex gap-6 overflow-x-auto py-6 px-1" style={{ background: t.tint }}>
        {books.map((p) => <Book key={p.id} p={p} cat={cat} />)}
      </div>
    </section>
  );
};

export default function Series() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () =>
    Promise.all([api.get("/categories"), api.get("/products")])
      .then(([c, p]) => { setCats(c.data); setItems(p.data); })
      .catch(() => setFailed(true));
  useEffect(() => { load(); }, []);
  const shelves = cats
    .map((c) => ({ cat: c, books: (items || []).filter((p) => p.category_id === c.id).sort((a, b) => num(a.issue_no) - num(b.issue_no) || a.title.localeCompare(b.title)) }))
    .filter((s) => s.books.length);
  return (
    <div>
      <PageHeader eyebrow="Does the series hang together?" title="The Series Shelf" />
      <div className="px-6 md:px-12 py-8" data-testid="series-page">
        {failed ? (
          <p className="font-body-serif italic text-lg text-[#9B2C2C]" data-testid="series-error">Couldn't load the shelf. Please refresh the page.</p>
        ) : items === null ? (
          <p className="font-body-serif italic text-lg text-[#5C5255]">Pulling the books off the shelf…</p>
        ) : shelves.length ? (
          shelves.map((s) => <Shelf key={s.cat.id} {...s} onEdit={setEditing} />)
        ) : (
          <p className="font-body-serif italic text-lg text-[#5C5255] py-20 text-center" data-testid="series-empty">No titles yet.</p>
        )}
      </div>
      <CategorySettingsDialog key={editing?.id || "none"} cat={editing} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />
    </div>
  );
}
